import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue3-toastify';

import { deviceOrchestratorGatewayService } from '~/services/device-orchestrator-gateway-api/device-orchestrator-gateway-api';
import { installationRiskService } from '~/services/installation-risk-api/installation-risk-api';
import { InstallationRiskApi } from '~/services/installation-risk-api/installation-risk-api.types';

// Constants
const TOKEN_ERROR_MESSAGE = 'Não foi possível obter o token do parceiro a plataforma.';
const PERMISSION_ERROR_MESSAGE = 'Sem permissão para executar esta ação. Apenas usuários do grupo N1_TECH_SUPPORT podem realizar esta operação.';

const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Inconsistências encontradas, não foi possível realizar a troca de número de série.',
  401: PERMISSION_ERROR_MESSAGE,
  403: PERMISSION_ERROR_MESSAGE,
  404: 'Projeto ou inversor não encontrado.',
  409: 'Conflito ao alterar número de série. Verifique se o novo SN não está em uso em outro projeto.',
  422: 'Validação falhou. Verifique os dados informados.',
} as const;

const QUERY_KEYS = {
  FINANCING: ['FINANCING'],
  hardwareForm: (projectId: string) => ['hardware-form', projectId],
  invertersInfo: (projectId: string) => ['inverters-info', projectId],
} as const;

// Types
interface ChangeInverterSerialNumberParams {
  projectId: string;
  partnerId: number;
  inverters: InstallationRiskApi.InstalledInverter[];
  request: InstallationRiskApi.ChangeInverterSerialNumberRequest;
}

interface TokenResult {
  brand: string;
  token: string | undefined;
}

// Helper functions
async function fetchPlatformAccessToken(
  partnerId: number,
  brand: string,
): Promise<TokenResult> {
  try {
    const response = await deviceOrchestratorGatewayService.getPlatformAccessToken({
      partnerId,
      brand,
    });

    if (!response.success || !response.data?.access_token) {
      console.warn(`Platform access token not available for brand ${brand}:`, response.message);
      return { brand, token: undefined };
    }

    return { brand, token: response.data.access_token };
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
    const errorStatus = error?.response?.status;
    
    console.warn(`Failed to get platform access token for brand ${brand} (partner ${partnerId}):`, {
      status: errorStatus,
      message: errorMessage,
      error,
    });
    
    return { brand, token: undefined };
  }
}

async function getPlatformAccessToken(
  partnerId: number,
  inverters: InstallationRiskApi.InstalledInverter[],
): Promise<string> {
  const uniqueBrands = [...new Set(inverters.map((inv) => inv.brand))];
  const tokenPromises = uniqueBrands.map((brand) => fetchPlatformAccessToken(partnerId, brand));
  const tokenResults = await Promise.all(tokenPromises);
  const token = tokenResults.find((result) => result.token)?.token;

  if (!token) {
    throw new Error(TOKEN_ERROR_MESSAGE);
  }

  return token;
}

function buildRequestWithToken(
  request: InstallationRiskApi.ChangeInverterSerialNumberRequest,
  platformAccessToken: string,
): InstallationRiskApi.ChangeInverterSerialNumberRequest {
  return {
    ...request,
    platform_access_token: platformAccessToken,
  };
}

function handleError(error: any): void {
  // Token error
  if (error?.message === TOKEN_ERROR_MESSAGE) {
    toast.error(TOKEN_ERROR_MESSAGE);
    return;
  }

  // Network/CORS errors
  if (!error?.response && error?.message) {
    if (error.message.includes('CORS') || error.message.includes('Network Error')) {
      toast.error('Erro de conexão com o servidor. Verifique se o serviço está disponível e se há problemas de CORS.');
      return;
    }
  }

  // HTTP status errors
  const status = error?.response?.status;
  const apiMessage = error?.response?.data?.message || error?.response?.data?.detail;
  const errorMessage = HTTP_ERROR_MESSAGES[status] || apiMessage || 'Erro inesperado ao trocar número de série.';
  
  toast.error(errorMessage);
}

function handleSuccess(
  data: InstallationRiskApi.ChangeInverterSerialNumberResponse,
  projectId: string,
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  toast.success(data.message || 'Número de série alterado com sucesso!');

  if (data.requires_manual_validation && data.manual_validation_message) {
    toast.warning(data.manual_validation_message, {
      autoClose: 10000,
    });
  }

  // Invalidate related queries
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FINANCING });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.hardwareForm(projectId) });
  queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invertersInfo(projectId) });
}

// Main hook
export default function useChangeInverterSerialNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, partnerId, inverters, request }: ChangeInverterSerialNumberParams) => {
      const platformAccessToken = await getPlatformAccessToken(partnerId, inverters);
      const requestWithToken = buildRequestWithToken(request, platformAccessToken);
      
      return installationRiskService.changeInverterSerialNumber(projectId, requestWithToken);
    },
    onError: handleError,
    onSuccess: (data, variables) => handleSuccess(data, variables.projectId, queryClient),
    retry: false,
  });
}

